import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';
import {
  isInFlightStage,
  isReviewActionable,
  resolveReviewPhase,
} from 'components/CallScribe/postCallReviewPhase';
import { NotRecordedReason } from 'components/CallScribe/notRecorded';
import { usePostCallIntelligence } from 'components/CallScribe/usePostCallIntelligence';
import Avatar from 'components/CallScribe/ui/Avatar';
import {
  CallScribeConsentResult,
  CallScribePipelineStage,
  ICallScribeCallListItem,
} from 'services/callScribe/types';

const STAGE_LABEL: Partial<Record<CallScribePipelineStage, string>> = {
  [CallScribePipelineStage.Received]: 'Preparing the recording',
  [CallScribePipelineStage.Transcribing]: 'Transcribing the call',
  [CallScribePipelineStage.Summarizing]: 'Drafting the summary and time entry',
};

const useCallEndedStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  const action = {
    flex: '1 1 0',
    minHeight: 52,
    border: 'none',
    borderRadius: 12,
    padding: theme.spacing(0, 2),
    fontFamily: 'inherit',
    fontSize: 16,
    lineHeight: '22px',
    fontWeight: CS_WEIGHT.strong,
    cursor: 'pointer',
    transition: 'background-color 150ms ease-out',
    '&:focus-visible': { outline: `2px solid ${t.stageFocus}`, outlineOffset: 2 },
  };
  return {
    root: {
      flex: '1 1 auto',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2.5),
      width: '100%',
      overflowY: 'auto',
      color: t.onGround,
    },
    head: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      paddingTop: theme.spacing(1),
      textAlign: 'center',
    },
    face: {
      display: 'grid',
      marginBottom: theme.spacing(1.5),
      borderRadius: '50%',
      boxShadow: `0 0 0 3px ${t.ground}, 0 0 0 5px ${t.glassBorder}`,
    },
    title: {
      margin: 0,
      ...CS_FONT.display,
      fontWeight: CS_WEIGHT.heavy,
      color: t.onGround,
    },
    sub: {
      ...CS_FONT.small,
      color: t.onGroundMuted,
      fontVariantNumeric: 'tabular-nums',
    },
    steps: {
      listStyle: 'none',
      margin: 0,
      padding: '4px 16px',
      borderRadius: 16,
      background: t.glass,
      boxShadow: `inset 0 0 0 1px ${t.glassBorder}`,
    },
    step: {
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '14px 0',
      '& + &': { borderTop: `1px solid ${t.glassBorder}` },
    },
    dot: {
      width: 28,
      height: 28,
      flex: '0 0 auto',
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      boxSizing: 'border-box',
      boxShadow: `inset 0 0 0 1.5px ${t.glassBorder}`,
      '& svg': { fontSize: 18 },
    },
    dotDone: {
      background: t.stageCall,
      color: t.stageCallInk,
      boxShadow: 'none',
    },
    dotNow: {
      boxShadow: `inset 0 0 0 2px ${t.stageFocus}`,
      '&::after': {
        content: '""',
        width: 12,
        height: 12,
        boxSizing: 'border-box',
        borderRadius: '50%',
        border: `2px solid ${t.stageFocus}`,
        borderRightColor: 'transparent',
        animation: '$spin 900ms linear infinite',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      },
    },
    '@keyframes spin': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    stepText: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    stepTitle: {
      ...CS_FONT.body,
      fontWeight: CS_WEIGHT.strong,
      color: t.onGround,
    },
    stepTitleNext: {
      color: t.onGroundMuted,
    },
    stepDetail: {
      ...CS_FONT.small,
      color: t.onGroundMuted,
    },
    hint: {
      margin: 0,
      ...CS_FONT.small,
      color: t.onGroundMuted,
      textAlign: 'center',
    },
    actions: {
      marginTop: 'auto',
      display: 'flex',
      gap: theme.spacing(1.25),
    },
    close: {
      ...action,
      background: t.glass,
      boxShadow: `inset 0 0 0 1px ${t.glassBorder}`,
      color: t.onGround,
      '&:hover': { background: t.glassStrong },
    },
    review: {
      ...action,
      background: t.onGround,
      color: t.ground,
      '&:hover': { background: t.onGroundMuted },
    },
    srOnly: {
      position: 'absolute',
      width: 1,
      height: 1,
      margin: -1,
      padding: 0,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
  };
});

const clock = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const spoken = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s} s`;
  return s === 0 ? `${m} min` : `${m} min ${s} s`;
};

export const summaryIsComing = (
  call: Partial<ICallScribeCallListItem>,
  stage: CallScribePipelineStage | undefined,
): boolean => {
  if (!call.answeredAtUtc || call.consentResult === CallScribeConsentResult.Declined) return false;
  if (call.hasRecording || isInFlightStage(stage)) return true;
  const reason = call.notRecordedReason;
  return reason === null || reason === undefined || reason === '' || reason === NotRecordedReason.RecordingPending;
};

export interface ICallEndedStepsProps {
  callId: number;
  who: string;
  closeLabel: string;
  onClose: () => void;
  children: React.ReactNode;
}

const CallEndedSteps: React.FC<ICallEndedStepsProps> = ({
  callId,
  who,
  closeLabel,
  onClose,
  children,
}) => {
  const classes = useCallEndedStyles();
  const [released, setReleased] = useState(false);
  const { reviewCall, pipelineStage, pipelineStalled, loadFailed, refresh } = usePostCallIntelligence(callId, !released);

  const stage = pipelineStage ?? reviewCall?.pipelineStage ?? undefined;
  const phase = resolveReviewPhase({
    hasIntelligence: reviewCall?.hasIntelligence,
    pipelineStage: stage,
    linkStatus: reviewCall?.linkStatus,
    consentResult: reviewCall?.consentResult,
    status: reviewCall?.status,
    answeredAtUtc: reviewCall?.answeredAtUtc,
    reconcileExhausted: pipelineStalled,
    willPostAutomatically: reviewCall?.willPostAutomatically,
  });
  const read = reviewCall !== undefined;
  const preparing = read &&
    summaryIsComing(reviewCall, stage) &&
    (phase === 'pending' || phase === 'generating') &&
    !pipelineStalled;

  useEffect(() => {
    if (read && !preparing) setReleased(true);
  }, [read, preparing]);

  if (!read && !released) {
    return (
      <div className={classes.root}>
        <div className={classes.head}>
          <span className={classes.face}>
            <Avatar name={who} tone={null} surface="stage" size="xl" />
          </span>
          <h2 className={classes.title}>Call ended</h2>
          {loadFailed ? (
            <span className={classes.sub} role="alert">We couldn&apos;t load this call.</span>
          ) : (
            <span className={classes.sub} role="status" aria-label="Loading the call">Loading the call…</span>
          )}
        </div>
        <div className={classes.actions}>
          <button type="button" className={classes.close} onClick={onClose}>
            {closeLabel}
          </button>
          {loadFailed ? (
            <button type="button" className={classes.review} onClick={() => { void refresh(); }}>
              Try again
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (released || !preparing) {
    return <>{children}</>;
  }

  const seconds =
    typeof reviewCall?.durationSeconds === 'number' && reviewCall.durationSeconds > 0
      ? reviewCall.durationSeconds
      : null;
  const writingDetail =
    phase === 'pending'
      ? 'Waiting for the recording'
      : (typeof stage === 'number' ? STAGE_LABEL[stage] : undefined) ??
        'Drafting the summary and time entry';
  const minutes = reviewCall?.suggestedDurationMinutes;
  const entryDetail = (() => {
    if (reviewCall?.willPostAutomatically === true) return 'It posts to the ticket on its own';
    if (typeof minutes === 'number' && minutes > 0) return `Suggested ${minutes} min`;
    return 'Duration and notes for you to check';
  })();

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        <span className={classes.face}>
          <Avatar name={who} tone={null} surface="stage" size="xl" />
        </span>
        <h2 className={classes.title}>Call ended</h2>
        <span className={classes.sub}>{seconds === null ? who : `${who} · ${clock(seconds)}`}</span>
      </div>

      <ol className={classes.steps} aria-label="After the call">
        <li className={classes.step}>
          <span className={`${classes.dot} ${classes.dotDone}`} aria-hidden>
            <CheckRoundedIcon fontSize="inherit" />
          </span>
          <span className={classes.stepText}>
            <span className={classes.stepTitle}>Call ended</span>
            <span className={classes.stepDetail}>{seconds === null ? who : spoken(seconds)}</span>
          </span>
        </li>
        <li className={classes.step} aria-current="step">
          <span className={`${classes.dot} ${classes.dotNow}`} aria-hidden />
          <span className={classes.stepText}>
            <span className={classes.stepTitle}>Writing your summary</span>
            <span className={classes.stepDetail}>{writingDetail}</span>
          </span>
        </li>
        <li className={classes.step}>
          <span className={classes.dot} aria-hidden />
          <span className={classes.stepText}>
            <span className={`${classes.stepTitle} ${classes.stepTitleNext}`}>
              Time entry ready to review
            </span>
            <span className={classes.stepDetail}>{entryDetail}</span>
          </span>
        </li>
      </ol>

      <p className={classes.hint}>
        Usually under a minute. Nothing is lost if you leave: the suggestion stays on the call in
        the ticket&apos;s call log.
      </p>

      <div
        className={classes.srOnly}
        role="status"
      >{`Writing your summary. ${writingDetail}.`}</div>

      <div className={classes.actions}>
        <button type="button" className={classes.close} onClick={onClose}>
          {closeLabel}
        </button>
        {isReviewActionable(phase) ? (
          <button
            type="button"
            className={classes.review}
            onClick={() => {
              setReleased(true);
            }}
          >
            Review now
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default CallEndedSteps;
